import React from 'react';
import styled from 'styled-components';
import { PlaceholderHeader } from '../ContentPlaceholder/PlaceholderHeader';
import { theme } from '../theme';
import { Avatar } from '../Avatar/Avatar';
import { EditableText } from '../EditableText/EditableText';

const HeaderElement = styled.div`
  display: grid;
  grid-template-areas:
    'image  label children'
    'image  heading children'
    'image  desc  children';
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-content: center;
  align-items: stretch;
  margin-bottom: 15px;
  background-color: white;
`;

const AvatarWrapperElement = styled.div<IProps>`
  grid-area: image;
  display: flex;
  align-items: center;
  padding-right: 15px;
  box-sizing: content-box;
  padding-bottom: ${props => (props.editable && props.label !== undefined ? '10px' : '0px')};
`;

const HeadingElement = styled.div`
  grid-area: heading;
  display: flex;
  font-weight: 600;
  font-size: 24px;
  border-radius: ${theme.vars.borderRadius.default};
  transition: ${theme.vars.transition.default};
  box-shadow: inset 0 1px 1px transparent;
  color: ${theme.vars.newColor.black};
`;

const EditWrapper = styled.div`
  grid-area: heading;
  display: flex;
  width: 100%;
  min-width: 0;
  transition: width 0.2s ease-out;
`;

const ChildrenElement = styled.div`
  grid-area: children;
  display: flex;
  align-items: center;
  padding-left: 15px;
`;

const DescriptionElement = styled.div<IProps>`
  font-size: 16px;
  grid-area: desc;
  margin-top: 5px;
  color: ${theme.vars.newColor.darkGS2};
  padding-left: ${props => (props.editable ? '6px' : '0px')};
`;

const LabelElement = styled.div<IProps>`
  font-size: 14px;
  grid-area: label;
  color: ${theme.vars.newColor.darkGS2};
  padding-left: ${props => (props.editable ? '6px' : '0px')};
`;

export interface IProps {
  /** The heading text */
  heading?: string;
  /** Render prop for custom headings that can include elements. */
  customHeading?: () => any;
  /** The heading label text - appears above the heading. */
  label?: string;
  /** The heading description text - appears underneath the heading. */
  description?: string;
  /** The avatar image. */
  avatarImage?: string;
  /** The avatar icon. */
  avatarIcon?: string;
  /** The avatar background color. */
  avatarBgColor?: string;
  /** When true, clicking on the header allows the user to change its contents. Defaults to false. */
  editable?: boolean;
  /** Whether to include top and bottom borders.  Defaults to false */
  borders?: boolean;
  /** When editable=true, called whenever the heading value changes. */
  onChange?: (value: string) => any;
  /** If true, displays content placeholder.  Defaults to false. */
  loading?: boolean;
}

export const Header: React.FC<IProps> = props => {
  const showAvatar = props.avatarImage != null || props.avatarIcon != null || props.avatarImage != null;

  if (props.loading) {
    return <PlaceholderHeader avatar={showAvatar} description={props.description != null} />;
  }

  return (
    <HeaderElement id="header">
      {props.label && <LabelElement editable={props.editable}>{props.label}</LabelElement>}
      {showAvatar && (
        <AvatarWrapperElement editable={props.editable} label={props.label} data-testid="Avatar">
          <Avatar size="large" icon={props.avatarIcon} bg={props.avatarBgColor} image={props.avatarImage} />
        </AvatarWrapperElement>
      )}
      {props.editable ? (
        <EditWrapper>
          <EditableText
            onChange={value => props.onChange(value)}
            value={props.heading}
            spacingBelow={false}
            inputTestId="HeadingInput"
          />
        </EditWrapper>
      ) : (
        <HeadingElement data-testid="Heading">
          {props.customHeading ? props.customHeading() : props.heading}
        </HeadingElement>
      )}
      <ChildrenElement>{props.children}</ChildrenElement>
      {props.description && <DescriptionElement editable={props.editable}>{props.description}</DescriptionElement>}
    </HeaderElement>
  );
};
